import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { KeyboardDatePicker, DatePicker } from '@material-ui/pickers';
import { CircularProgress } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

import AutocompleteFamily from './AutocompleteFamily';
import { enhancedReducer } from '../../../../utils';
import { useAddCropMutation, useUpdateCropMutation } from '../../../../adapters/crops';
import { useIrrigationTypes } from '../../../../adapters/irrigation-types';
import { useSoilTypes } from '../../../../adapters/soil-types';

const useStyles = makeStyles((theme) => ({
	dialogContent: {
		paddingTop: theme.spacing(2),
	},
	section: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
	sectionTitle: {
		fontWeight: 600,
		marginBottom: theme.spacing(1),
	},
}));

const insertInit = {
	family: null,
	name: '',
	area: null,
	variety: null,
	harvested_qty: 0,
	sowing: null,
	closure: null,
	campaign: '',
	irrigation_type_id: null,
	soil_type_id: null,
};

const CropFormDialog = ({ open, onClose, crop, parcel, refetch }) => {
	const classes = useStyles();
	const { t } = useTranslation(['translation', 'soil', 'irrigation']);

	const [state, updateState] = React.useReducer(
		enhancedReducer,
		{},
		() => (!crop ? { parcel: parcel.id, ...insertInit } : crop)
	);
	const [selectedDate, handleDate] = React.useState(null);
	const [openDateCampaign, setOpenDateCampaign] = React.useState(false);

	const [campaignErrorStatus, setCampaignErrorStatus] = React.useState(false);
	const [cropAutoCmpErrorStatus, setCropAutoCmpErrorStatus] = React.useState(false);
	const [sowingErrorStatus, setSowingErrorStatus] = React.useState(false);
	const errorMessage = t('Field_required');

	const insertModel = useAddCropMutation();
	const updateModel = useUpdateCropMutation();

	// Fetch lookup data from API
	const { isLoading: irrigationLoading, data: irrigationTypes = [] } = useIrrigationTypes();
	const { isLoading: soilLoading, data: soilTypes = [] } = useSoilTypes();

	// Reset state when crop changes (switching between add/edit or different crops)
	React.useEffect(() => {
		if (open) {
			// Reset state based on whether we're adding or editing
			const initialState = !crop
				? { parcel: parcel.id, ...insertInit }
				: {
						...crop,
						// Extract IDs from nested objects for the form
						irrigation_type_id: crop.irrigation_type?.id ?? null,
						soil_type_id: crop.soil_type?.id ?? null,
				  };
			updateState({
				type: 'reset',
				_value: initialState,
			});

			// Initialize selectedDate from campaign if editing
			if (crop?.campaign) {
				const yearMatch = crop.campaign.match(/^(\d{4})/);
				if (yearMatch) {
					const year = parseInt(yearMatch[1], 10);
					handleDate(new Date(year, 0, 1));
				}
			} else {
				handleDate(null);
			}

			// Reset error states
			setCampaignErrorStatus(false);
			setCropAutoCmpErrorStatus(false);
			setSowingErrorStatus(false);
		}
	}, [open, crop, parcel.id]);

	React.useEffect(() => {
		if (selectedDate) {
			setOpenDateCampaign(false);
			updateState({
				_path: 'campaign',
				_value: `${selectedDate?.getFullYear()}/${selectedDate?.getFullYear() + 1}`,
				type: 'text',
			});
		}
	}, [selectedDate]);

	React.useEffect(() => {
		if (insertModel.isSuccess || updateModel.isSuccess) {
			insertModel.reset();
			updateModel.reset();
			refetch();
			onClose();
		}
	}, [insertModel.isSuccess, updateModel.isSuccess, onClose, refetch]);

	const validateData = () => {
		let validationFlag = true;
		if (!selectedDate) {
			validationFlag = false;
			setCampaignErrorStatus(true);
		}
		if (!state.family) {
			validationFlag = false;
			setCropAutoCmpErrorStatus(true);
		}
		if (!state.sowing) {
			validationFlag = false;
			setSowingErrorStatus(true);
		}
		return validationFlag;
	};

	const handleSave = () => {
		if (validateData()) {
			// Find the full objects for irrigation_type and soil_type
			const selectedIrrigationType = state.irrigation_type_id
				? irrigationTypes.find((type) => type.id === state.irrigation_type_id)
				: null;
			const selectedSoilType = state.soil_type_id
				? soilTypes.find((type) => type.id === state.soil_type_id)
				: null;

			// Prepare payload with full objects instead of just IDs
			const { irrigation_type_id, soil_type_id, ...restState } = state;
			const payload = {
				...restState,
				irrigation_type: selectedIrrigationType
					? { id: selectedIrrigationType.id, name: selectedIrrigationType.name }
					: null,
				soil_type: selectedSoilType
					? { id: selectedSoilType.id, name: selectedSoilType.name }
					: null,
			};

			if (crop) {
				// Ensure ID is included for update
				const updateData = { ...payload, id: crop.id };
				updateModel.mutate(updateData);
			} else {
				insertModel.mutate(payload);
			}
		}
	};

	const handleTextChange = React.useCallback(({ target: { value, name } }) => {
		const updatePath = name.split('.');
		updateState({
			_path: updatePath,
			_value: value,
			type: 'text',
		});
	}, []);

	const handleDateChange = React.useCallback((value, name) => {
		try {
			handleTextChange({
				target: { value: format(value, 'yyyy-MM-dd'), name: name },
			});
		} catch (error) {
			handleTextChange({ target: { value: value, name: name } });
		}
	}, [handleTextChange]);

	const handleAutocompleteChange = React.useCallback((newValue) => {
		setCropAutoCmpErrorStatus(false);
		updateState({
			_path: newValue._path.split('.'),
			_value: newValue._value,
			type: 'text',
		});
	}, []);

	const handleSelectChange = React.useCallback(({ target: { name, value } }) => {
		updateState({
			_path: [name],
			_value: value === '' ? null : value,
			type: 'text',
		});
	}, []);

	const handleClose = () => {
		if (!insertModel.isLoading && !updateModel.isLoading) {
			onClose();
		}
	};

	const isLoading = insertModel.isLoading || updateModel.isLoading || irrigationLoading || soilLoading;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>{crop ? t('Edit Crop') : t('Add New Crop')}</DialogTitle>
			<DialogContent className={classes.dialogContent}>
				<div className={classes.section}>
					<Typography variant="subtitle2" className={classes.sectionTitle}>
						{t('Basic Information')}
					</Typography>
					<Divider />
				</div>

				<Grid container spacing={2}>
					<Grid item xs={12}>
						<DatePicker
							views={['year']}
							value={selectedDate}
							onChange={handleDate}
							onAccept={() => setCampaignErrorStatus(false)}
							onClose={() => setOpenDateCampaign(false)}
							autoOk
							open={openDateCampaign}
							TextFieldComponent={(props) => (
								<TextField
									label={t('Crop.campaign')}
									value={
										selectedDate
											? `${selectedDate?.getFullYear()}/${
													selectedDate?.getFullYear() + 1
											  }`
											: state.campaign
									}
									fullWidth
									onFocus={() => setOpenDateCampaign(true)}
									error={campaignErrorStatus}
									helperText={campaignErrorStatus ? errorMessage : null}
									required
								/>
							)}
						/>
					</Grid>

					<Grid item xs={12}>
						<AutocompleteFamily
							handleAutocompleteChange={handleAutocompleteChange}
							initValue={state.family}
							path="family"
							editError={cropAutoCmpErrorStatus}
							editDisabled={false}
							editErrorText={cropAutoCmpErrorStatus && errorMessage}
						/>
					</Grid>

					<Grid item xs={12}>
						<TextField
							label={t('Crop.variety/hybrid')}
							name="variety"
							value={state.variety || ''}
							onChange={handleTextChange}
							fullWidth
						/>
					</Grid>
				</Grid>

				<div className={classes.section}>
					<Typography variant="subtitle2" className={classes.sectionTitle}>
						{t('Timeline')}
					</Typography>
					<Divider />
				</div>

				<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<KeyboardDatePicker
							name="sowing"
							label={t('Crop.seedtime')}
							value={state.sowing}
							onChange={(date) => handleDateChange(date, 'sowing')}
							onAccept={() => setSowingErrorStatus(false)}
							format="dd/MM/yyyy"
							variant="inline"
							error={sowingErrorStatus}
							helperText={sowingErrorStatus ? errorMessage : null}
							fullWidth
							autoOk
							required
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<KeyboardDatePicker
							name="closure"
							label={t('Crop.harvest date')}
							value={state.closure}
							onChange={(date) => handleDateChange(date, 'closure')}
							format="dd/MM/yyyy"
							variant="inline"
							fullWidth
							autoOk
						/>
					</Grid>
				</Grid>

				<div className={classes.section}>
					<Typography variant="subtitle2" className={classes.sectionTitle}>
						{t('Performance')}
					</Typography>
					<Divider />
				</div>

				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextField
							label={t('Crop.average performance')}
							name="harvested_qty"
							value={state.harvested_qty || ''}
							onChange={handleTextChange}
							type="number"
							fullWidth
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">t/ha</InputAdornment>
								),
							}}
						/>
					</Grid>
				</Grid>

				<div className={classes.section}>
					<Typography variant="subtitle2" className={classes.sectionTitle}>
						{t('Crop.agricultural_settings')}
					</Typography>
					<Divider />
				</div>

				<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<FormControl fullWidth size="small">
							<InputLabel>{t('Crop.irrigation_type')}</InputLabel>
							<Select
								name="irrigation_type_id"
								value={state.irrigation_type_id ?? ''}
								onChange={handleSelectChange}
								label={t('Crop.irrigation_type')}
							>
								<MenuItem value="">
									<em>{t('Common.none')}</em>
								</MenuItem>
								{irrigationTypes.map((type) => (
									<MenuItem key={type.id} value={type.id}>
										{t(type.name, { ns: 'irrigation' })}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} sm={6}>
						<FormControl fullWidth size="small">
							<InputLabel>{t('Crop.soil_type')}</InputLabel>
							<Select
								name="soil_type_id"
								value={state.soil_type_id ?? ''}
								onChange={handleSelectChange}
								label={t('Crop.soil_type')}
							>
								<MenuItem value="">
									<em>{t('Common.none')}</em>
								</MenuItem>
								{soilTypes.map((type) => (
									<MenuItem key={type.id} value={type.id}>
										{t(type.name, { ns: 'soil' })}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
				</Grid>
			</DialogContent>

			<DialogActions>
				<Button onClick={handleClose} disabled={isLoading}>
					{t('Cancel')}
				</Button>
				<Button
					onClick={handleSave}
					color="primary"
					variant="contained"
					disabled={isLoading}
					startIcon={
						isLoading ? <CircularProgress size={20} color="inherit" /> : null
					}
				>
					{t('Save')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CropFormDialog;
